/*
 * Copyright Evollis 2021
 * All Rights Reserved.
 *
 * All information contained herein is, and remains the property of Evollis.
 * The intellectual and technical concepts contained herein are
 * proprietary to Evollis and are protected by trade secret and copyright law.
 * Dissemination of this information or reproduction of this material
 * is STRICTLY FORBIDDEN unless prior written permission is obtained
 * from Evollis.
 */
import { isValidIban, loc } from "../../../../core/utils"
import { DATE_FORMAT } from "../../constants/ClientPageConstants"
import genericSchema from "../ClientPageValidator"
import moment from "moment"
import * as yup from "yup"

/* Error messages */
const requiredMessage = () => loc`Required`
const dateYYYYFormatValidationMessage = () => loc`Not a valid YYYY date`
const invalidBirthDateMessage = () => loc`birthDate is not valid`
const maximumCharactersMessage = (maximumValue) => loc`Maximum` + " " + maximumValue + " " + loc`characters`
const minimumCharactersMessage = (minimalValue) => minimalValue + " " + loc`minimum characters`
const ibanMustHave24CharsMessage = () => loc`IBAN must have 24 characters`
const regexMessage = () => loc`Unauthorized characters`
const postalCodeMessage = () => loc`Invalid postal code`
const ibanWrongFormatMessage = () => loc`Not a valid IBAN`
const futureResidenceDateMessage = () => loc`This date cannot be in future`
const yearSeniorityBeforeBirthDateMessage = () => loc`Year seniority bank cannot be before birth date`
const wrongDiffMessageBankSeniorty = () =>
  loc`difference between bank seniority date and birth date must be at least 16 years`
const invalidFormat = () => loc`Invalid format`

const invalideDateErrorMessage = () => loc`Not a valid date`
const birthDateMinMessage = () => loc`Minimum age must be > 18 years old`
const birthDateMaxessage = () => loc`Maximum age must be < 110 years old`

const phoneNumberMessage = () => loc`Invalid phone number`
const emailNotValidMessage = () => loc`Email invalid`
const futureEmploymentDateMessage = () => loc`Employment date cannot be in future`
const employmentDateBeforeBirthDateMessage = () => loc`Employment date cannot be before birth date`
const residenceDateBeforeBirthDateMessage = () => loc`Residence date cannot be before birth date`
const wrongDiffMessage = () => loc`difference between employment date and birth date must be at least 16 years`
const childrenContainsMessage = () => loc`number of children must be contains between 0 and 10`
const numberMessage = () => loc`Must be a number`
const numberIntegerMessage = () => loc`Must be a whole number`
const mustBePositiveMessage = () => loc`Must be positive`
const bicWrongFormatMessage = () => loc`Not a valid BIC`

// regex
const defaultRegex =
  /^[a-zA-ZêëéèçÊËÁÉÈÇñàáâäÀÂ' ÄùûüÙúíñÑ¿¡ÁÛÜìïîÌÏÎòóÓôöÒÔÖ]+(([ -][a-zA-Z êëéèçÊËÉÈÇàâáäÀÂÄùÁûüÙúíñÑ¿¡ÛÜìïîÌÏñÎòôóÓöÒÔÖ])?[a-zA-ZêëéèçÊËÉÈÇñàâáäÀÁÂÄúíñÑ¿¡ùûüÙÛÜìïîÌÏÎòôÓöÒóÔÖ]*)*$/
const postalCodeRegex = /^[0-9]{5}$/
const taxNumberDNIRegex = /^[0-9]{8}[A-Za-z]{1}$/
const taxNumberNIERegex = /^(x|X)|(y|Y)|(z|Z){1}[0-9]{7}[A-Za-z]{1}$/
const spainPhoneRegex = /^(34|0034)[67][0-9]{8}$/
const emailRegex = /^[a-zA-Z0-9_\-.]+@[a-zA-Z0-9_-]+\.[a-zA-Z0-9_-]+$/
const bicRegex = /^[A-Z]{6,6}[A-Z2-9][A-NP-Z0-9]([A-Z0-9]{3,3}){0,1}$/

let schema = yup.object().shape({
  identity: yup.object().shape({
    name: yup
      .string()
      .nullable()
      .required(requiredMessage)
      .min(2, () => minimumCharactersMessage(2))
      .max(32, () => maximumCharactersMessage(32))
      .matches(defaultRegex, { message: regexMessage }),
    secondSurname: yup
      .string()
      .nullable()
      .transform((v) => (v === "" || v == null ? undefined : v))
      .optional()
      .min(2, () => minimumCharactersMessage(2))
      .max(32, () => maximumCharactersMessage(32))
      .matches(defaultRegex, { message: regexMessage }),
    firstName: yup
      .string()
      .nullable()
      .min(2, () => minimumCharactersMessage(2))
      .max(32, () => maximumCharactersMessage(32))
      .required(requiredMessage)
      .matches(defaultRegex, { message: regexMessage }),
    citizenShipCountry: yup.string().nullable().required(requiredMessage),
    taxNumber: yup
      .string()
      .nullable()
      .required(requiredMessage)
      .test({
        name: "taxNumber",
        message: "",
        test(value) {
          if (!value) {
            return this.createError({
              message: requiredMessage,
              path: "identity.taxNumber"
            })
          }
          if (value?.match(taxNumberDNIRegex) || value?.match(taxNumberNIERegex)) {
            return true
          } else {
            return this.createError({
              message: invalidFormat,
              path: "identity.taxNumber"
            })
          }
        }
      }),
    identificationExpirationDate: yup
      .string()
      .nullable()
      .required(requiredMessage)
      .test({
        name: "identificationExpirationDateTest",
        message: "",
        test(value) {
          const idExpirationDate = moment(value, DATE_FORMAT.identity.identificationExpirationDate.ES)
          if (!idExpirationDate.isValid()) {
            return this.createError({
              message: requiredMessage,
              path: "identity.identificationExpirationDate"
            })
          }
        }
      }),
    birthDate: yup
      .string()
      .nullable()
      .test({
        name: "ageTest",
        message: "",
        test(value) {
          if (!value) {
            return this.createError({
              message: requiredMessage,
              path: "identity.birthDate"
            })
          }
          const birthDate = moment(value, DATE_FORMAT.identity.birthDate.BE)
          if (!birthDate.isValid() || birthDate.year() < 1000 || birthDate.year() > 9999) {
            return this.createError({
              message: invalideDateErrorMessage,
              path: "identity.birthDate"
            })
          }
          let currentDate = moment()
          if (!(currentDate.diff(birthDate, "years") >= 18)) {
            return this.createError({
              message: birthDateMinMessage,
              path: "identity.birthDate"
            })
          }
          if (!(currentDate.diff(birthDate, "years") < 110)) {
            return this.createError({
              message: birthDateMaxessage,
              path: "identity.birthDate"
            })
          }
        }
      })
  }),
  coordinates: yup.object().shape({
    address: yup.string().nullable().required(requiredMessage).max(32, () => maximumCharactersMessage(32)),
    additionalAddress: yup.string().nullable().max(32, () => maximumCharactersMessage(32)),
    locality: yup.string().nullable().required(requiredMessage).max(32, () => maximumCharactersMessage(32)),
    countryOfResidence: yup.string().nullable().required(requiredMessage),
    postalCode: yup
      .string()
      .nullable()
      .required(requiredMessage)
      .test({
        name: "postal code",
        message: "",
        test(value) {
          if (!value) return true
          if (!String(value).match(postalCodeRegex)) {
            return this.createError({
              message: postalCodeMessage(),
              path: "coordinates.postalCode"
            })
          }
          return true
        }
      }),
    phoneNumber: yup
      .string()
      .nullable()
      .required(requiredMessage)
      .test({
        name: "phone verification",
        message: "",
        test(value) {
          const phoneValueWithoutSpaces = value?.replace(/[^\d]/g, "")
          if (!phoneValueWithoutSpaces?.match(spainPhoneRegex)) {
            return this.createError({
              message: phoneNumberMessage,
              path: "coordinates.phoneNumber"
            })
          }
          return true
        }
      }),
    email: yup
      .string()
      .nullable()
      .required(requiredMessage)
      .test({
        name: "email",
        message: "",
        test(value) {
          if (!value?.match(emailRegex)) {
            return this.createError({
              message: emailNotValidMessage,
              path: "coordinates.email"
            })
          }
          return true
        }
      }),
    iban: yup
      .string()
      .nullable()
      .min(24, ibanMustHave24CharsMessage)
      .max(24, ibanMustHave24CharsMessage)
      .test({
        name: "iban format verification",
        message: "",
        test(value) {
          if (!value) {
            return this.createError({
              message: requiredMessage,
              path: "coordinates.iban"
            })
          }
          // eslint-disable-next-line no-new-wrappers
          let formatedBic = new String(value).toString().split(" ").join("")
          if (!isValidIban(formatedBic)) {
            return this.createError({
              message: ibanWrongFormatMessage,
              path: "coordinates.iban"
            })
          }
        }
      }),
    bic: yup
      .string()
      .nullable()
      .required(requiredMessage)
      .max(11, () => maximumCharactersMessage(11))
      .test({
        name: "bic format verification",
        message: "",
        test(value) {
          if (value && !value.toUpperCase().match(bicRegex)) {
            return this.createError({
              message: bicWrongFormatMessage,
              path: "coordinates.bic"
            })
          }
        }
      })
      .required(requiredMessage),
    yearBankSeniority: yup
      .string()
      .nullable()
      .test({
        name: "yearbankseniority verification",
        message: "",
        test(value) {
          if (!value) {
            return this.createError({
              message: requiredMessage,
              path: "coordinates.yearBankSeniority"
            })
          }
          const yearbankseniority = moment(value)
          if (!yearbankseniority.isValid() || yearbankseniority.year() < 1000 || yearbankseniority.year() > 9999) {
            return this.createError({
              message: dateYYYYFormatValidationMessage,
              path: "coordinates.yearBankSeniority"
            })
          }
          // date must not be in future
          if (yearbankseniority.isAfter(moment())) {
            return this.createError({
              message: futureResidenceDateMessage,
              path: "coordinates.yearBankSeniority"
            })
          }

          // birthDate must be valid
          const birthDate = moment(this.options.context.identity.birthDate)
          if (!this.options.context.identity.birthDate) {
            return this.createError({
              message: invalidBirthDateMessage,
              path: "coordinates.yearBankSeniority"
            })
          }

          //birthDate must be before bank seniority Date
          if (birthDate.isAfter(yearbankseniority)) {
            return this.createError({
              message: yearSeniorityBeforeBirthDateMessage,
              path: "coordinates.yearBankSeniority"
            })
          }

          //diff between birthDate and bank seniority date must be > 16 years
          const diff = yearbankseniority.year() - birthDate.year()
          if (diff < 16) {
            return this.createError({
              message: wrongDiffMessageBankSeniorty,
              path: "coordinates.yearBankSeniority"
            })
          }
        }
      })
  }),
  situation: yup.object().shape({
    profession: yup.string().nullable().required(requiredMessage),
    employmentDate: yup
      .string()
      .nullable()
      .test({
        name: "employment date verification",
        message: "",
        test(value) {
          if (!value) {
            return this.createError({
              message: requiredMessage,
              path: "situation.employmentDate"
            })
          }
          const employmentDate = moment(value)
          if (employmentDate.isAfter(moment())) {
            return this.createError({
              message: futureEmploymentDateMessage,
              path: "situation.employmentDate"
            })
          }
          const birthDate = moment(this.options.context.identity.birthDate)
          if (!this.options.context.identity.birthDate) {
            return this.createError({
              message: invalidBirthDateMessage,
              path: "situation.employmentDate"
            })
          }
          if (birthDate.isAfter(employmentDate)) {
            return this.createError({
              message: employmentDateBeforeBirthDateMessage,
              path: "situation.employmentDate"
            })
          }
        }
      }),
    residenceDate: yup
      .string()
      .nullable()
      .test({
        name: "residenceDate verification",
        message: "",
        test(value) {
          if (!value) {
            return this.createError({
              message: requiredMessage,
              path: "situation.residenceDate"
            })
          }
          const residenceDate = moment(
            value,
            DATE_FORMAT.situation.residenceDate[this.options.context.countryLanguage],
            true
          )
          if (
            !residenceDate.isValid() ||
            (residenceDate.isValid() && (residenceDate.year() < 1000 || residenceDate.year() > 9999))
          ) {
            return this.createError({
              message: dateYYYYFormatValidationMessage,
              path: "situation.residenceDate"
            })
          }
          if (residenceDate.isAfter(moment())) {
            return this.createError({
              message: futureResidenceDateMessage,
              path: "situation.residenceDate"
            })
          }
          const birthDate = moment(
            this.options.context.identity.birthDate,
            DATE_FORMAT.identity.birthDate[this.options.context.countryLanguage],
            true
          )
          if (!this.options.context.identity.birthDate || !birthDate.isValid()) {
            return this.createError({
              message: invalidBirthDateMessage,
              path: "situation.residenceDate"
            })
          }
          if (birthDate.isAfter(residenceDate)) {
            return this.createError({
              message: residenceDateBeforeBirthDateMessage,
              path: "situation.residenceDate"
            })
          }
          //diff between birthDate and residenceDate must be > 16 years
          const diff = residenceDate.year() - birthDate.year()
          if (diff < 16) {
            return this.createError({
              message: wrongDiffMessage,
              path: "situation.residenceDate"
            })
          }
        }
      }),
    companyName: yup.string().required(requiredMessage),
    workingSituation: yup.string().nullable().required(requiredMessage),
    companyCity: yup.string().required(requiredMessage),
    dependentChildrenNumber: yup
      .number()
      .nullable()
      .required(requiredMessage)
      .min(0, () => childrenContainsMessage())
      .max(10, () => childrenContainsMessage())
  }),
  budget: yup.object().shape({
    netMonthlyIncome: yup.number().nullable().required(requiredMessage),
    otherIncome: yup
      .number(numberMessage)
      .typeError(numberMessage)
      .nullable()
      .integer(numberIntegerMessage)
      .required(requiredMessage)
      .moreThan(-1, mustBePositiveMessage),
    rent: yup
      .number(numberMessage)
      .typeError(numberMessage)
      .nullable()
      .integer(numberIntegerMessage)
      .required(requiredMessage)
      .moreThan(-1, mustBePositiveMessage),
    otherCharges: yup
      .number(numberMessage)
      .typeError(numberMessage)
      .nullable()
      .integer(numberIntegerMessage)
      .required(requiredMessage)
      .moreThan(-1, mustBePositiveMessage),
    numberMonthsIncome: yup.object().nullable().required(requiredMessage)
  })
})

export default (values) => schema.concat(genericSchema).validate(values, { abortEarly: false, context: values })
